import { forwardRef } from 'react';
import { Input, type InputProps } from './Input';

export interface SearchInputProps extends Omit<InputProps, 'icon' | 'type'> {
  /** When provided together with a non-empty value, renders a clear (✕) button. */
  onClear?: () => void;
  /** Accessible label for the clear button. Defaults to "Clear search". */
  clearLabel?: string;
  /**
   * Compact bordered look for a list-toolbar search box (the pattern established by the patient
   * directory): 38px on desktop, 42px on mobile, `--surface` fill instead of the default `--surface-2`.
   */
  toolbarSize?: 'desktop' | 'mobile';
}

const TOOLBAR_SIZE_CLASSNAME: Record<NonNullable<SearchInputProps['toolbarSize']>, string> = {
  desktop: 'h-[38px] rounded-[9px] border border-[var(--border)] bg-[var(--surface)] text-[13.5px] hover:bg-[var(--surface)]',
  mobile: 'h-[42px] rounded-[9px] border border-[var(--border)] bg-[var(--surface)] hover:bg-[var(--surface)]',
};

function SearchGlyph() {
  return (
    <svg
      className="h-[18px] w-[18px]"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.25"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="11" cy="11" r="7" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  );
}

/**
 * SearchInput — the shared search field: a text input with a leading search
 * glyph and an optional clear button. Wraps the design-system {@link Input} so
 * every search box across the app looks and behaves the same instead of each
 * screen re-wiring an `<Input>` with its own icon.
 */
export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
  ({ onClear, clearLabel = 'Clear search', value, className, toolbarSize, ...props }, ref) => {
    const hasValue = value != null && value !== '';
    const showClear = Boolean(onClear) && hasValue;
    const toolbarClassName = toolbarSize ? TOOLBAR_SIZE_CLASSNAME[toolbarSize] : '';

    return (
      <div className="relative">
        <Input
          ref={ref}
          type="search"
          value={value}
          icon={<SearchGlyph />}
          // WebKit/Chromium render their own "x" for type="search" once it has a value, which doubled
          // up with our clear button below — disable the native one so there's only ever one.
          className={`[&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-cancel-button]:hidden ${showClear ? 'pr-[42px] ' : ''}${toolbarClassName} ${className ?? ''}`}
          {...props}
        />
        {showClear && (
          <button
            type="button"
            aria-label={clearLabel}
            onClick={onClear}
            className="absolute right-[12px] top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-[7px] text-[var(--text-3)] transition-colors hover:bg-[var(--surface-3)] hover:text-[var(--text)] focus:outline-none focus:ring-2 focus:ring-[var(--ring)]"
          >
            <svg
              className="h-4 w-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>
    );
  }
);

SearchInput.displayName = 'SearchInput';
